import { useEffect } from 'react';
import { ExternalLink, Star } from 'lucide-react';

/**
 * Elfsight Google Reviews widget loader.
 *
 * Para mostrar las reseñas REALES de Google en lugar de placeholders falsos:
 *  1. Cree el widget en https://elfsight.com/google-reviews-widget/
 *     conectándolo al perfil real "Punto Hispano | Gestoría y Asesoría en Zúrich".
 *  2. Copie el ID del widget (formato: aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa).
 *  3. Defina la variable de entorno VITE_ELFSIGHT_GOOGLE_REVIEWS_ID con ese valor,
 *     o pase el prop `widgetId`.
 *
 * Mientras el widget no esté configurado, mostramos un fallback honesto que enlaza
 * directamente a Google Maps — nunca reseñas inventadas.
 */
interface ElfsightGoogleReviewsProps {
  widgetId?: string;
  googleMapsUrl?: string;
  rating?: number;
  totalReviews?: number;
}

const DEFAULT_GOOGLE_URL =
  'https://www.google.com/maps/place/Punto+Hispano+%7C+Gestor%C3%ADa+y+Asesor%C3%ADa+en+Z%C3%BArich/@47.4253,8.5635,17z';

const ElfsightGoogleReviews = ({
  widgetId,
  googleMapsUrl = DEFAULT_GOOGLE_URL,
  rating = 4.9,
  totalReviews = 160,
}: ElfsightGoogleReviewsProps) => {
  const id =
    widgetId ||
    (import.meta.env.VITE_ELFSIGHT_GOOGLE_REVIEWS_ID as string | undefined) ||
    'd8502716-dfd6-4e8c-b338-b5ce98cf0091';

  useEffect(() => {
    if (!id) return;
    if (document.querySelector('script[src*="elfsightcdn.com/platform.js"]')) return;
    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    document.body.appendChild(script);
  }, [id]);

  if (id) {
    return (
      <div>
        <div className={`elfsight-app-${id}`} data-elfsight-app-lazy />
      </div>
    );
  }

  // Fallback: mostrar SOLO el rating real con CTA a Google Maps. Nunca placeholders.
  return (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
      <div className="inline-flex items-center gap-3 bg-white dark:bg-white/10 px-5 py-3 rounded-full border border-gray-200 dark:border-white/15 shadow-sm">
        <span className="font-bold text-gray-900 dark:text-white text-2xl">{rating.toFixed(1)}</span>
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-4 h-4 ${
                star <= Math.round(rating)
                  ? 'text-amber-400 fill-amber-400'
                  : 'text-gray-300 dark:text-gray-600'
              }`}
            />
          ))}
        </div>
        <span className="text-gray-500 dark:text-gray-400 text-sm">
          ({totalReviews} reseñas en Google)
        </span>
      </div>
      <p className="mt-4 text-sm text-gray-500 dark:text-white/60 max-w-md">
        Mostramos únicamente reseñas verificadas. Léalas todas directamente en nuestro perfil oficial
        de Google Maps.
      </p>
      <a
        href={googleMapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-punto text-white text-sm font-semibold hover:bg-punto/90 transition-colors"
      >
        <ExternalLink className="w-4 h-4" />
        Ver reseñas reales en Google Maps
      </a>
    </div>
  );
};

export default ElfsightGoogleReviews;
